import axios from 'axios'

const FETCH_TIMEOUT = 60000 // 60 seconds

/**
 * Get API base URL from environment variable
 * In development, use empty string to let Vite proxy handle requests
 * In production, use the configured API URL
 */
export const getApiBaseUrl = () => {
  // In development mode, return empty string to use Vite proxy
  if (import.meta.env.DEV) {
    return ''
  }
  // In production, use configured URL or default
  return import.meta.env.VITE_API_URL || 'http://localhost:8000'
}

/**
 * Alias for getApiBaseUrl (for compatibility with original code)
 */
export const getApiBase = getApiBaseUrl

/**
 * Fetch with timeout utility
 * Also tracks loading state automatically
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @param {boolean} silentLoading - If true, won't trigger global loading spinner
 */
export const fetchWithTimeout = async (url, options = {}, timeout = FETCH_TIMEOUT, silentLoading = false) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  // Start loading (only if not silent)
  if (!silentLoading && loadingCallbacks.start) {
    loadingCallbacks.start()
  }

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    })
    clearTimeout(timeoutId)

    // Stop loading on success (only if not silent)
    if (!silentLoading && loadingCallbacks.stop) {
      loadingCallbacks.stop()
    }

    return response
  } catch (error) {
    clearTimeout(timeoutId)

    // Stop loading on error (only if not silent)
    if (!silentLoading && loadingCallbacks.stop) {
      loadingCallbacks.stop()
    }

    if (error.name === 'AbortError') {
      throw new Error(`Requisição expirou após ${timeout / 1000}s - possível problema com o banco de dados`)
    }
    throw error
  }
}

/**
 * Create axios instance with default config
 */
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: FETCH_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Store loading callbacks (will be set by setupLoadingInterceptors)
let loadingCallbacks = {
  start: null,
  stop: null
}

/**
 * Setup loading interceptors with callbacks from LoadingContext
 * This should be called once when the app initializes
 */
export const setupLoadingInterceptors = (startLoading, stopLoading) => {
  loadingCallbacks.start = startLoading
  loadingCallbacks.stop = stopLoading
}

/**
 * Add request interceptor to include auth token and track loading
 */
api.interceptors.request.use(
  (config) => {
    // Start loading only if not silent
    if (!config.silentLoading && loadingCallbacks.start) {
      loadingCallbacks.start()
    }

    // Company frontend always uses the company access token
    const token = localStorage.getItem('access_token')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    // Stop loading on error (only if not silent)
    if (!error.config?.silentLoading && loadingCallbacks.stop) {
      loadingCallbacks.stop()
    }
    return Promise.reject(error)
  }
)

/**
 * Add response interceptor for error handling and stop loading
 */
api.interceptors.response.use(
  (response) => {
    // Stop loading on success (only if not silent)
    if (!response.config.silentLoading && loadingCallbacks.stop) {
      loadingCallbacks.stop()
    }
    return response
  },
  (error) => {
    // Stop loading on error (only if not silent)
    if (!error.config?.silentLoading && loadingCallbacks.stop) {
      loadingCallbacks.stop()
    }

    if (error.code === 'ECONNABORTED') {
      throw new Error(`Requisição expirou após ${FETCH_TIMEOUT / 1000}s - possível problema com o banco de dados`)
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Não redirecionar se for erro de login (endpoint de login deve tratar o erro)
      const isLoginEndpoint = error.config?.url?.includes('/login') || error.config?.url?.includes('/auth/login')

      if (!isLoginEndpoint) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('company_id')
        localStorage.removeItem('user_info')
        window.location.href = '/login'
      }
    }

    throw error
  }
)

/**
 * Generic API call helper (legacy compatibility)
 */
export const apiCall = async (method, path, data = null, token = null) => {
  const config = {
    method,
    url: path,
    headers: {}
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (data) {
    config.data = data
  }

  try {
    const response = await api.request(config)
    return response.data
  } catch (error) {

    throw error.response?.data || error
  }
}

export default api
